import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import { useSiteContent } from "../../lib/content";
import { emailHref, phoneHref } from "../../lib/siteContent";
import { getSupabase, isSupabaseConfigured } from "../../lib/supabase";
import { isPtPhone } from "../../lib/utils";
import { notifyRepairReceived } from "../../lib/whatsappApi";
import type { RepairRequestInsert } from "../../types";
import { Button } from "../ui/Button";
import { Container, Section } from "../ui/Container";
import { SectionHeading } from "../ui/SectionHeading";

const equipmentOptions = [
  "Computador de Secretária",
  "Portátil",
  "Rede",
  "Outro",
] as const;

const categoryOptions = [
  "Hardware",
  "Software",
  "Vírus/Malware",
  "Rede",
  "Recuperação de Dados",
  "Não sei",
] as const;

const schema = z.object({
  nome: z.string().trim().min(3, "Indica o nome completo."),
  telefone: z
    .string()
    .trim()
    .refine(isPtPhone, "Usa um telefone português válido (9 dígitos, opcional +351)."),
  email: z.string().trim().email("Indica um email válido."),
  tipo_equipamento: z.enum(equipmentOptions),
  categoria_problema: z.enum(categoryOptions),
  descricao: z.string().trim().min(10, "Descreve o problema com um pouco mais de detalhe."),
  urgencia: z.enum(["normal", "urgente"]),
  preferencia_contacto: z.enum(["telefone", "email", "whatsapp"]),
  disponibilidade_data: z.string().optional(),
  disponibilidade_periodo: z.preprocess(
    (value) => (value === "" ? undefined : value),
    z.enum(["manha", "tarde", "noite"]).optional(),
  ),
});

type FormValues = z.infer<typeof schema>;

const fieldClass =
  "mt-1 w-full rounded-lg border bg-background px-3 py-2.5 text-sm text-primary placeholder:text-muted/70";
const labelClass = "block text-sm font-medium text-primary";
const errorClass = "mt-1 text-xs text-urgent";

export function Contact() {
  const { content } = useSiteContent();
  const contact = content.contact;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      urgencia: "normal",
      preferencia_contacto: "telefone",
      tipo_equipamento: "Portátil",
      categoria_problema: "Não sei",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    if (!isSupabaseConfigured) {
      setSubmitError("O envio ainda não está configurado (faltam as chaves Supabase).");
      return;
    }

    const payload: RepairRequestInsert = {
      nome: values.nome,
      telefone: values.telefone,
      email: values.email,
      tipo_equipamento: values.tipo_equipamento,
      categoria_problema: values.categoria_problema,
      descricao: values.descricao,
      urgencia: values.urgencia,
      preferencia_contacto: values.preferencia_contacto,
      disponibilidade_data: values.disponibilidade_data || null,
      disponibilidade_periodo: values.disponibilidade_periodo ?? null,
      status: "pendente",
    };

    const { error } = await getSupabase().from("repair_requests").insert(payload);
    if (error) {
      setSubmitError("Não foi possível enviar o pedido. Tenta novamente ou usa o telefone / WhatsApp.");
      return;
    }
    void notifyRepairReceived({
      nome: payload.nome,
      telefone: payload.telefone,
      tipo_equipamento: payload.tipo_equipamento,
      categoria_problema: payload.categoria_problema,
      urgencia: payload.urgencia,
      disponibilidade_data: payload.disponibilidade_data,
      disponibilidade_periodo: payload.disponibilidade_periodo,
    });
    setSuccess(true);
  };

  return (
    <Section id="contacto" labelledBy="contacto-title" className="bg-surface/40">
      <Container>
        <SectionHeading
          id="contacto-title"
          title={contact.heading}
          subtitle={contact.subtitle}
        />

        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          {success ? (
            <div className="rounded-2xl border border-accent-terminal/30 bg-background p-8">
              <CheckCircle2 className="h-10 w-10 text-accent-terminal" aria-hidden />
              <h3 className="mt-4 font-heading text-2xl font-semibold text-primary">Pedido recebido!</h3>
              <p className="mt-2 text-muted">
                Entramos em contacto em breve para confirmar o diagnóstico. Se o WhatsApp
                estiver ligado, também recebes uma confirmação por mensagem.
              </p>
            </div>
          ) : (
            <form className="space-y-4 rounded-2xl border bg-background p-5 md:p-8" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div>
                <label className={labelClass} htmlFor="nome">
                  Nome completo
                </label>
                <input id="nome" className={fieldClass} autoComplete="name" {...register("nome")} />
                {errors.nome && <p className={errorClass}>{errors.nome.message}</p>}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="telefone">
                    Telefone
                  </label>
                  <input
                    id="telefone"
                    className={fieldClass}
                    autoComplete="tel"
                    placeholder="912 345 678"
                    {...register("telefone")}
                  />
                  {errors.telefone && <p className={errorClass}>{errors.telefone.message}</p>}
                </div>
                <div>
                  <label className={labelClass} htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    className={fieldClass}
                    autoComplete="email"
                    {...register("email")}
                  />
                  {errors.email && <p className={errorClass}>{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="tipo_equipamento">
                    Tipo de equipamento
                  </label>
                  <select id="tipo_equipamento" className={fieldClass} {...register("tipo_equipamento")}>
                    {equipmentOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="categoria_problema">
                    Categoria do problema
                  </label>
                  <select id="categoria_problema" className={fieldClass} {...register("categoria_problema")}>
                    {categoryOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="descricao">
                  Descrição do problema
                </label>
                <textarea
                  id="descricao"
                  rows={5}
                  className={fieldClass}
                  placeholder="O que acontece, desde quando, e o que já tentaste."
                  {...register("descricao")}
                />
                {errors.descricao && <p className={errorClass}>{errors.descricao.message}</p>}
              </div>

              <fieldset>
                <legend className={labelClass}>Urgência</legend>
                <div className="mt-2 flex gap-4 text-sm text-muted">
                  <label className="flex items-center gap-2">
                    <input type="radio" value="normal" {...register("urgencia")} />
                    Normal
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" value="urgente" {...register("urgencia")} />
                    Urgente
                  </label>
                </div>
              </fieldset>

              <fieldset>
                <legend className={labelClass}>Preferência de contacto</legend>
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted">
                  <label className="flex items-center gap-2">
                    <input type="radio" value="telefone" {...register("preferencia_contacto")} />
                    Telefone
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" value="email" {...register("preferencia_contacto")} />
                    Email
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" value="whatsapp" {...register("preferencia_contacto")} />
                    WhatsApp
                  </label>
                </div>
              </fieldset>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="disponibilidade_data">
                    Disponibilidade (preferência)
                  </label>
                  <input
                    id="disponibilidade_data"
                    type="date"
                    className={fieldClass}
                    {...register("disponibilidade_data")}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="disponibilidade_periodo">
                    Período do dia
                  </label>
                  <select
                    id="disponibilidade_periodo"
                    className={fieldClass}
                    {...register("disponibilidade_periodo")}
                  >
                    <option value="">Sem preferência</option>
                    <option value="manha">Manhã</option>
                    <option value="tarde">Tarde</option>
                    <option value="noite">Noite</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-muted">
                Isto é só uma preferência. O agendamento definitivo é confirmado depois do contacto.
              </p>

              {submitError && <p className={errorClass}>{submitError}</p>}

              <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto">
                {isSubmitting ? "A enviar…" : "Enviar pedido"}
              </Button>
            </form>
          )}

          <aside className="rounded-2xl border bg-background p-6">
            <h3 className="font-heading text-xl font-semibold text-primary">Contacto directo</h3>
            <ul className="mt-5 space-y-3 text-sm text-muted">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-primary" aria-hidden />
                <a href={phoneHref(contact.phone)}>{contact.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-primary" aria-hidden />
                <a href={emailHref(contact.email)}>{contact.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 text-primary" aria-hidden />
                <span>
                  Zona de atuação: <strong className="text-primary">{contact.zone}</strong>
                </span>
              </li>
            </ul>
            <div className="mt-6 overflow-hidden rounded-xl border bg-surface">
              <div className="flex h-48 items-center justify-center bg-[radial-gradient(circle_at_center,rgb(var(--border)),transparent_70%)] p-6 text-center">
                <p className="whitespace-pre-line text-sm text-muted">{contact.coverage}</p>
              </div>
            </div>
          </aside>
        </div>
      </Container>
    </Section>
  );
}
